/**
 * usePopupLifecycle - Hook for popup window lifecycle management
 *
 * Handles:
 * - Notifying main window when popup closes
 * - Sending heartbeat every 30 seconds to keep popup registered as active
 * - Tracking connection state to main window (via heartbeat ACK)
 *
 * Should be used in all popup page components.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  PopupActions,
  useMainWindowMessages,
  MessageType,
  PopupMessage,
} from './PopupEvents';

const HEARTBEAT_INTERVAL_MS = 30000;
const HEARTBEAT_TIMEOUT_MS = 5000; // How long to wait for ACK before considering disconnected
const MAX_MISSED_HEARTBEATS = 2; // How many missed ACKs before showing disconnected state

export interface PopupConnectionState {
  /** Whether we have a confirmed connection to main window */
  isConnected: boolean;
  /** Whether we're waiting for initial connection */
  isConnecting: boolean;
  /** Number of consecutive missed heartbeat ACKs */
  missedHeartbeats: number;
}

export const usePopupLifecycle = (
  popupId: string | null,
  popupType: string,
): PopupConnectionState => {
  const [connectionState, setConnectionState] = useState<PopupConnectionState>({
    isConnected: true, // Assume connected initially
    isConnecting: false,
    missedHeartbeats: 0,
  });

  const pendingHeartbeatRef = useRef<boolean>(false);
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Listen for heartbeat ACKs from main window
  useMainWindowMessages(
    useCallback((msg: PopupMessage) => {
      if (msg.type === MessageType.HEARTBEAT_ACK) {
        pendingHeartbeatRef.current = false;
        if (heartbeatTimeoutRef.current) {
          clearTimeout(heartbeatTimeoutRef.current);
          heartbeatTimeoutRef.current = null;
        }
        setConnectionState({
          isConnected: true,
          isConnecting: false,
          missedHeartbeats: 0,
        });
      }
    }, []),
    popupId ?? undefined,
    [popupId],
  );

  // Notify main window when popup closes
  useEffect(() => {
    if (!popupId) return;

    const handleBeforeUnload = () => {
      PopupActions.notifyClosed(popupId);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [popupId]);

  // Send heartbeat and track ACK response
  useEffect(() => {
    if (!popupId) return;

    const sendHeartbeatWithTracking = () => {
      pendingHeartbeatRef.current = true;
      PopupActions.heartbeat(popupId, popupType);

      // Set timeout to detect missed ACK
      heartbeatTimeoutRef.current = setTimeout(() => {
        if (pendingHeartbeatRef.current) {
          setConnectionState((prev) => {
            const newMissed = prev.missedHeartbeats + 1;
            return {
              isConnected: newMissed < MAX_MISSED_HEARTBEATS,
              isConnecting: newMissed >= MAX_MISSED_HEARTBEATS,
              missedHeartbeats: newMissed,
            };
          });
        }
      }, HEARTBEAT_TIMEOUT_MS);
    };

    // Send initial heartbeat immediately to establish connection
    sendHeartbeatWithTracking();

    // Then continue with regular interval
    const interval = setInterval(
      sendHeartbeatWithTracking,
      HEARTBEAT_INTERVAL_MS,
    );

    return () => {
      clearInterval(interval);
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
      }
    };
  }, [popupId, popupType]);

  return connectionState;
};
